import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { renderThrough, type RenderElement } from '../../util/render/index.ts';

/**
 * One entry of a navigation, rendered as whatever element the page routes with.
 *
 *   <NavigationItem render={<Link href={url} title={title} />}>{label}</NavigationItem>
 *   <NavigationItem render={<button type="button" onClick={goTo} />}>{label}</NavigationItem>
 *
 * A navigation entry is a link in most storefronts and, in some, a control that
 * leaves the SPA entirely — which router, and which of the two, is the app's
 * decision and never this package's. So the element arrives through `render`
 * and no enum, no url helper and no navigation function crosses into here.
 *
 * It paints nothing of its own: an entry's look belongs to the navigation
 * composing it, and this carries only the slot every navigation is read by.
 */

export type NavigationItemProps = ComponentProps<'a'> & {
  /** The element the page navigates with. Required — there is no neutral entry. */
  readonly render: RenderElement;
};

export const NavigationItem = ({ className, render, ...props }: NavigationItemProps) =>
  renderThrough(render, 'navigation-item', clsxm(className), props);
